import type {
  StaffAttentionSeverity,
  StaffAttentionSource,
  StaffAttentionSummary,
} from "@lafayette/schemas";
import { Link } from "react-router";

import { getAdminPublicConfig } from "../config/public-config";
import { navIconFor } from "../navigation/nav-icons";
import { type AdminArea, areaPath, isAreaOffered } from "../navigation/offering";
import { useAreaHref, useOffering } from "../navigation/use-offering";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { cn } from "../ui/cn";
import { Icon } from "../ui/icon";
import { StatusBadge, type StatusTone } from "../ui/status-badge";
import {
  areaDescriptions,
  countText,
  itemArea,
  itemCodeText,
  itemLink,
  severityLabel,
  sourceCopy,
} from "./labels";

// The sections of Home that stay as they were before the dashboard (CHT-767): the queues that could not be read, the information queues,
// the areas of the role, the support facts and the time of the answer. The dashboard (`HomeLayout`) holds the new cards.

/** Always UTC and 24 hours, so the text does not depend on the browser's time zone. */
export function updatedText(iso: string): string {
  const time = new Date(iso);
  const hh = String(time.getUTCHours()).padStart(2, "0");
  const mm = String(time.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm} UTC`;
}

function sinceText(iso: string | null): string | null {
  return iso === null ? null : `since ${iso.slice(0, 16).replace("T", " ")} UTC`;
}

// A severity is a glyph and a word (StatusBadge), and the card keeps a coloured left edge as a second cue.
const severityTone: Readonly<Record<StaffAttentionSeverity, StatusTone>> = {
  urgent: "danger",
  attention: "warning",
  info: "info",
};
const severityEdge: Readonly<Record<StaffAttentionSeverity, string>> = {
  urgent: "border-l-destructive",
  attention: "border-l-warning",
  info: "border-l-info",
};

const sectionHeading = "m-0 text-xl leading-7 font-semibold tracking-tight";

function SourceCard({ source }: Readonly<{ source: StaffAttentionSource }>) {
  const areaHref = useAreaHref();
  const copy = sourceCopy[source.key];
  const listHref = areaHref(copy.area, copy.link);
  const heading = `${countText(source)} ${copy.label(source.count)}`;
  return (
    <li
      className={cn(
        "grid content-start gap-3 rounded-md border border-l-4 border-border bg-card p-4 shadow-sm sm:p-5",
        severityEdge[source.severity],
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <h3 className="m-0 text-lg leading-7 font-semibold tracking-tight">
          {listHref === null ? (
            <span className="inline-flex min-h-11 items-center compact:min-h-8">{heading}</span>
          ) : (
            <Link
              to={listHref}
              className="inline-flex min-h-11 items-center text-foreground underline-offset-4 hover:underline compact:min-h-8"
            >
              {heading}
            </Link>
          )}
        </h3>
        <StatusBadge tone={severityTone[source.severity]}>
          {severityLabel[source.severity]}
        </StatusBadge>
      </div>
      <p className="m-0 text-sm leading-6 text-muted-foreground">{copy.hint}</p>
      {source.items.length > 0 ? (
        <ul
          className="m-0 grid list-none gap-0 border-t border-border p-0 pt-1"
          aria-label={`First ${copy.label(source.count)}`}
        >
          {source.items.map((item) => {
            const href = areaHref(itemArea(item), itemLink(item));
            return (
              <li
                key={`${item.kind}-${item.id}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-0"
              >
                {href === null ? (
                  <span className="inline-flex min-h-11 items-center font-serif text-lg font-medium compact:text-base compact:min-h-8">
                    {item.customer.displayName}
                  </span>
                ) : (
                  <Link
                    to={href}
                    className="inline-flex min-h-11 items-center font-serif text-lg font-medium compact:text-base text-foreground compact:min-h-8"
                  >
                    {item.customer.displayName}
                  </Link>
                )}
                <span className="text-sm text-muted-foreground">
                  {[itemCodeText(item.kind, item.code), sinceText(item.since)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

/** The queues that could not be read: said plainly, never as zero, with the way to read them again. */
export function BlindWarning({
  blind,
  onRefresh,
}: Readonly<{ blind: readonly StaffAttentionSource[]; onRefresh: () => void }>) {
  if (blind.length === 0) return null;
  return (
    <section
      className="grid max-w-3xl gap-2 rounded-md border border-destructive-border bg-destructive-soft p-4 text-destructive sm:p-5"
      role="alert"
      aria-labelledby="home-blind"
    >
      <h2 id="home-blind" className={cn(sectionHeading, "text-destructive")}>
        Some queues could not be read
      </h2>
      <ul className="m-0 list-disc pl-5 text-sm leading-6">
        {blind.map((source) => (
          <li key={source.key}>{sourceCopy[source.key].label(0)}</li>
        ))}
      </ul>
      <p className="m-0 text-sm leading-6">
        Their counts are not shown, so they are not zero. Try again.
      </p>
      <div>
        <Button type="button" variant="outline" onClick={onRefresh}>
          Try again
        </Button>
      </div>
    </section>
  );
}

/** The information queues (a count that is not work yet), below the work. */
export function InformationSection({ summary }: Readonly<{ summary: StaffAttentionSummary }>) {
  const info = summary.sources.filter(
    (source) =>
      source.status === "ok" && source.severity === "info" && (source.count > 0 || source.capped),
  );
  if (info.length === 0) return null;
  return (
    <section aria-labelledby="home-info" className="grid gap-3">
      <h2 id="home-info" className={sectionHeading}>
        For your information
      </h2>
      <ul className="m-0 grid list-none gap-4 p-0 lg:grid-cols-2">
        {info.map((source) => (
          <SourceCard key={source.key} source={source} />
        ))}
      </ul>
    </section>
  );
}

function AreaTile({ item }: Readonly<{ item: AdminArea }>) {
  return (
    <li className="relative grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-1 rounded-md border border-border bg-card p-4 shadow-sm focus-within:border-ring hover:border-primary">
      <span
        aria-hidden="true"
        className="row-span-2 grid size-9 place-items-center rounded-sm bg-accent text-link"
      >
        <Icon name={navIconFor(item.key)} size="lg" />
      </span>
      {/* The link covers the whole tile (the `after` layer), so the tile is one large target; its name is the area, nothing else. */}
      <Link
        to={item.path}
        className="min-w-0 font-semibold text-foreground no-underline after:absolute after:inset-0 after:content-['']"
      >
        {item.label}
      </Link>
      <span className="text-sm leading-5 text-muted-foreground">
        {areaDescriptions[item.key] ?? ""}
      </span>
    </li>
  );
}

export function AreasSection({ areas }: Readonly<{ areas: readonly AdminArea[] }>) {
  return (
    <section aria-labelledby="home-areas" className="grid gap-3">
      <h2 id="home-areas" className={sectionHeading}>
        Your areas
      </h2>
      <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 xl:grid-cols-3">
        {areas.map((item) => (
          <AreaTile key={item.key} item={item} />
        ))}
      </ul>
    </section>
  );
}

export function SupportSection() {
  const offering = useOffering();
  const config = getAdminPublicConfig();
  const canOpenOperations = isAreaOffered("operations", offering);
  return (
    <section aria-labelledby="home-support">
      <Card>
        <CardHeader>
          <CardTitle id="home-support">Support information</CardTitle>
          <CardDescription>
            Give these four facts to support when you report a problem.{" "}
            {canOpenOperations ? (
              <>
                Provider status is in{" "}
                <Link
                  className="text-link underline underline-offset-4 hover:no-underline"
                  to={areaPath("operations")}
                >
                  Operations
                </Link>
                .
              </>
            ) : (
              "Provider status is available to supervisors in Operations."
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            {[
              ["Environment", config.environment],
              ["Version", config.version],
              ["Build", config.buildId],
              ["Commit", config.gitSha.slice(0, 12)],
            ].map(([term, value]) => (
              <div key={term} className="grid min-w-0 gap-0.5">
                <dt className="text-xs font-bold tracking-[0.08em] text-muted-foreground uppercase">
                  {term}
                </dt>
                <dd className="m-0 font-medium tabular-nums [overflow-wrap:anywhere]">{value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </section>
  );
}

/** When the answer was made, and the button that reads it again. */
export function RefreshLine({
  generatedAt,
  fetching,
  onRefresh,
}: Readonly<{ generatedAt: string; fetching: boolean; onRefresh: () => void }>) {
  return (
    <p className="m-0 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <span>Updated {updatedText(generatedAt)}.</span>
      <Button type="button" variant="ghost" size="sm" onClick={onRefresh} disabled={fetching}>
        <Icon name="refresh" className={fetching ? "animate-spin" : undefined} />
        {fetching ? "Refreshing…" : "Refresh"}
      </Button>
    </p>
  );
}
